import { useState, useMemo, useRef, useEffect } from 'react'
import { X, File, FileCheck, FileSpreadsheet, FileCog, CirclePlus, CircleMinus } from '@/components/icons'
import { Button } from './ui/button'
import { SearchInput } from './ui/search-input'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { useDocumentGetAll } from '../lib/hooks'
import {
  useTripGetById,
  useTripUpdateComplianceDocuments,
  useTripLegGetById,
  useTripLegUpdateComplianceDocuments,
} from '../lib/hooks/trip'
import { toast } from 'sonner'

interface AddComplianceDocumentToTripDrawerProps {
  isOpen: boolean
  onClose: () => void
  tripId?: string // link selection to a trip
  tripLegId?: string // link selection to a flight
  stagedSelectedIds?: string[] // create flow: seed the selection from staged state
  onStageSelection?: (documentIds: string[]) => void // create flow: return selection, no network
}

// Icon mapping based on document category
const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Manual':
      return FileCog
    case 'Checklist':
      return FileCheck
    case 'Form':
      return FileSpreadsheet
    default:
      return File
  }
}

export function AddComplianceDocumentToTripDrawer({
  isOpen,
  onClose,
  tripId,
  tripLegId,
  stagedSelectedIds,
  onStageSelection,
}: AddComplianceDocumentToTripDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([])
  const prevIsOpen = useRef(isOpen)

  // Fetch all compliance documents
  const { data: allDocuments = [], isPending: documentsLoading } = useDocumentGetAll('Compliance')

  // Fetch trip / trip leg to get already-added documents
  const { data: trip } = useTripGetById(tripId ?? '')
  const { data: tripLeg } = useTripLegGetById(tripLegId)

  // Mutations for updating compliance documents
  const updateDocumentsMutation = useTripUpdateComplianceDocuments(tripId ?? '')
  const updateLegDocumentsMutation = useTripLegUpdateComplianceDocuments()

  // Existing selection: staged state (create flow) > flight docs > trip docs
  const existingDocumentIds = onStageSelection
    ? (stagedSelectedIds ?? [])
    : tripLegId
      ? tripLeg?.documents?.map((doc) => doc.id) || []
      : trip?.documents?.map((doc) => doc.id) || []

  // Reset selected documents when drawer opens to match existing documents
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      setSelectedDocumentIds(existingDocumentIds)
    }
    prevIsOpen.current = isOpen
  }, [isOpen, existingDocumentIds])

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return allDocuments

    const query = searchQuery.toLowerCase()
    return allDocuments.filter((doc) =>
      doc.title.toLowerCase().includes(query) ||
      (doc.subType ?? '').toLowerCase().includes(query)
    )
  }, [allDocuments, searchQuery])

  const handleClose = () => {
    setSearchQuery('')
    setSelectedDocumentIds([])
    onClose()
  }

  const handleToggleDocument = (documentId: string) => {
    setSelectedDocumentIds((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    )
  }

  const handleSave = async () => {
    try {
      if (onStageSelection) {
        onStageSelection(selectedDocumentIds)
      } else if (tripLegId) {
        await updateLegDocumentsMutation.mutateAsync({
          tripLegId,
          documentIds: selectedDocumentIds,
        })
      } else if (tripId) {
        await updateDocumentsMutation.mutateAsync({
          tripId,
          documentIds: selectedDocumentIds,
        })
      }

      toast.success('Documents updated successfully')
      handleClose()
    } catch (error) {
      console.error('Error updating documents:', error)
      toast.error('Failed to update documents')
    }
  }

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="bg-surface-L0 rounded-tl-2xl rounded-tr-2xl shadow-lg max-h-[85dvh] flex flex-col">
        {/* Header */}
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            Add Documents
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
          <div className="flex flex-col gap-3 p-2">
            {/* Search Input */}
            <SearchInput
              placeholder="Search documents"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Document List */}
            {documentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-gray-300 border-t-foreground rounded-full animate-spin" />
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-content-primary-subtle text-sm">
                  {searchQuery ? 'No documents found' : 'No documents available'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border-L1">
                {filteredDocuments.map((document, index) => {
                  const isSelected = selectedDocumentIds.includes(document.id)
                  const IconComponent = getCategoryIcon(document.subType ?? '')
                  const CheckIconComponent = isSelected ? CircleMinus : CirclePlus

                  return (
                    <div key={document.id}>
                      <button
                        onClick={() => handleToggleDocument(document.id)}
                        className={`my-2 w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors min-h-9 ${
                          isSelected
                            ? 'bg-surface-L2 hover:bg-surface-L1 active:bg-surface-L1'
                            : 'hover:bg-surface-L2 active:bg-surface-L2'
                        }`}
                      >
                        {/* Left Icon */}
                        <IconComponent className="size-5 text-content-primary-bold shrink-0" />

                        {/* Document Info */}
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-content-primary-bold text-sm leading-normal truncate">
                            {document.title}
                          </p>
                          <p className="text-content-primary-subtle text-xs leading-normal truncate">
                            {document.subType}
                          </p>
                        </div>

                        {/* Right Icon - Plus/Minus */}
                        <CheckIconComponent className="size-5 text-content-primary-bold shrink-0" />
                      </button>
                      {index < filteredDocuments.length - 1 && (
                        <div className="border-t border-secondary" />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <DrawerFooter className="border-t border-border-L2">
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="mini"
              onClick={handleClose}
              className='flex-1'
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="mini"
              onClick={handleSave}
              disabled={updateDocumentsMutation.isPending || updateLegDocumentsMutation.isPending}
              className="flex-1"
            >
              {updateDocumentsMutation.isPending || updateLegDocumentsMutation.isPending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
